import type { MenuItem } from "./types";

/** Lower case, accents stripped, apostrophes dropped, other punctuation as spaces, spaces collapsed. */
export function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/['’`´]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The text a search reads: name, description, ingredients and every wine field. */
function searchable(item: MenuItem): string[] {
  const w = item.wine;
  return [
    item.name, item.description, ...item.ingredients,
    ...(w ? [w.producer, w.place, w.region, w.country, w.grape, w.vintage] : []),
  ];
}

/** The items any of whose texts holds the query, accents and case aside, in the order given. */
export function searchItems(items: MenuItem[], query: string): MenuItem[] {
  const q = normalize(query);
  if (!q) return items;
  return items.filter((item) => searchable(item).some((text) => normalize(text).includes(q)));
}

/** A wine category: a drink category whose items carry wine details. It has no Riordina. */
export function isWineCategory(isDrink: boolean, items: MenuItem[]): boolean {
  return isDrink && items.some((i) => i.wine !== null);
}

/**
 * Every sibling in order after a move among some of them: the moved ones (a tab's root categories) fill the places
 * they held, in their new order, and the rest (the other tab's) stay where they were. The server wants every sibling.
 */
export function mergeSiblingOrder(siblings: string[], moved: string[]): string[] {
  const shown = new Set(moved);
  const queue = [...moved];
  return siblings.map((id) => (shown.has(id) ? queue.shift() ?? id : id));
}

/** Splits a pasted list on commas and new lines, dropping the empty pieces. */
export function splitIngredients(text: string): string[] {
  return text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
}
